import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import CalendarCheckYellowCircleIcon from '@icons/CalendarCheckYellowCircleIcon';
import CallBellYellowCircleIcon from '@icons/CallBellYellowCircleIcon';
import FavoriteIcon from '@icons/FavoriteIcon';
import PersonCheckYellowCircleIcon from '@icons/PersonCheckYellowCircleIcon';
import ShareIcon from '@icons/ShareIcon';
import LinkCTA from '@atoms/LinkCTA';
import { BodyLarge, ButtonNoStyles, ButtonOutlineLarge, ButtonPrimaryLarge, H3 } from '@styled';

// Helpers
import { mediaQueries } from '@general/breakpoints';
import colors from '@general/colors';
import { getDateRangeTextInEastern } from '@general/dates';
import { getAttendeeListUrl } from '@general/helpers/event.helpers';
import { transformLocation } from '@general/location';
import getFavoriteHook from '@hooks/getFavoriteHook';
import BadgeYellowCircleIcon from '@icons/BadgeYellowCircleIcon';

// Functionality
import useIcscTracking from '@tracking/useIcscTracking';

const Container = styled.div`
    box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.166029);
    height: 100%;
    padding-bottom: 65px;
    position: relative;
`;

const Stripe = styled.hr`
    border-top: 6px solid ${colors.primary};
    margin: 0;
`;

const BannerImage = styled.img`
    height: 229px;
    max-width: 100%;
    min-width: 100%;
    object-fit: cover;
`;

const Body = styled.div`
    padding: 28px 32px;
`;

const BodyDetails = styled(BodyLarge)`
    min-height: 112px;
`;

const EventDetailsButton = styled(ButtonPrimaryLarge)`
    margin-bottom: 1rem;
    margin-right: 0;
    padding: 1rem 3rem;
    width: 100%;

    @media ${mediaQueries.md} {
        margin-bottom: 0;
        margin-right: 1rem;
        width: calc(50% - 0.5rem);
    }
`;

const MyAgendaButton = styled(ButtonOutlineLarge)`
    min-height: auto;
    min-width: auto;
    padding: 1rem 3rem;
    width: 100%;

    @media ${mediaQueries.md} {
        width: calc(50% - 0.5rem);
    }
`;

const Footer = styled.div`
    align-items: center;
    border-top: 1px solid rgb(216, 216, 216, 0.3);
    bottom: 0;
    display: flex;
    justify-content: space-between;
    padding: 8px 30px;
    position: absolute;
    width: 100%;
`;

const FooterIconButton = styled(ButtonNoStyles)`
    align-items: center;
    display: flex;
    height: 40px;
    justify-content: center;
    width: 40px;
`;

const FavoriteIconContainer = styled.div`
    align-items: center;
    display: flex;
    height: 40px;
    justify-content: center;
    width: 40px;
`;

const EventCardWithCTAs = ({ event, showShareIcon = false }) => {
    const {
        recordId,
        code,
        title,
        url,
        beginsAt,
        endsAt,
        image,
        location: { address, city, stateProvince, country },
        bookHotelLink,
        isFavorite: initialIsFavorite,
    } = event;

    const history = useHistory();
    const useFavorite = getFavoriteHook({ type: 'event', initialIsFavorite });
    const { isFavorite, isLoadingFavorite, saveFavorite, removeFavorite, setIsFavoriteManually } = useFavorite();
    const { trackEvent } = useIcscTracking();

    useEffect(() => {
        setIsFavoriteManually(initialIsFavorite);
    }, [initialIsFavorite, setIsFavoriteManually]);

    const goToEventDetails = () => {
        trackEvent({
            exclude: ['Algolia'],
            click: {
                target: 'click_event_details',
                value: 'Click Event Details',
            },
        });
        history.push(`/my-icsc/events/${code}`);
    };

    const goToEventAgenda = () => {
        history.push(`/my-icsc/events/${code}/agenda`);
    };

    const toggleFavorite = () => {
        if (isFavorite) {
            removeFavorite(recordId);
        } else {
            saveFavorite(recordId);
        }
    };

    return (
        <Container className="event-card-with-ctas">
            <Stripe />

            <BannerImage src={image || '/assets/images/pattern-white.svg'} alt={title} />

            <Body>
                <H3 className="bold mb-3">{title}</H3>
                <BodyDetails className="mb-2rem text-background">
                    {getDateRangeTextInEastern(beginsAt, endsAt)}
                    <br />
                    {address}
                    <br />
                    {transformLocation({ city, stateProvince, country })}
                </BodyDetails>

                <div className="d-flex flex-wrap mb-2rem">
                    <EventDetailsButton onClick={() => goToEventDetails()}>Event Details</EventDetailsButton>
                    <MyAgendaButton onClick={() => goToEventAgenda()}>My Agenda</MyAgendaButton>
                </div>

                <div className="event-card-ctas d-flex justify-content-center px-1.25rem">
                    <LinkCTA
                        Icon={CalendarCheckYellowCircleIcon}
                        className="mr-1.875rem"
                        href={`/my-icsc/events/${code}/sessions`}
                        text="Schedule"
                        style={{ width: '65px' }}
                    />

                    <LinkCTA
                        Icon={BadgeYellowCircleIcon}
                        className="mr-1.875rem"
                        href="/my-icsc/events/badge"
                        text="Badge"
                        style={{ width: '65px' }}
                        onClick={e => {
                            e.preventDefault();
                            history.push('/my-icsc/events/badge-preview');
                        }}
                    />

                    <LinkCTA
                        Icon={PersonCheckYellowCircleIcon}
                        className="mr-1.875rem"
                        href={getAttendeeListUrl(url)}
                        text="Attendees"
                        style={{ width: '65px' }}
                    />

                    {/* <ButtonCTA Icon={ClockFaceYellowCircleIcon} text="Meetings" onClick={() => alert('Meetings')} /> */}

                    {bookHotelLink && (
                        <LinkCTA
                            Icon={CallBellYellowCircleIcon}
                            href={bookHotelLink}
                            text="Book Hotel"
                            target="_blank"
                            style={{ width: '65px' }}
                        />
                    )}
                </div>
            </Body>

            <Footer className="event-card-footer">
                <div>
                    {/*
                    <ButtonNoStyles className="text-sm" onClick={() => alert('Cancel/Refund')}>
                        Cancel/Refund
                    </ButtonNoStyles>
                    */}
                </div>

                <div className="d-flex">
                    {showShareIcon && (
                        <FooterIconButton>
                            <ShareIcon url={url} />
                        </FooterIconButton>
                    )}

                    <FavoriteIconContainer>
                        <FavoriteIcon
                            id={recordId}
                            type="event"
                            isLoggedIn
                            isFavorite={isFavorite}
                            isLoading={isLoadingFavorite}
                            onClick={toggleFavorite}
                        />
                    </FavoriteIconContainer>
                </div>
            </Footer>
        </Container>
    );
};

EventCardWithCTAs.propTypes = {
    event: PropTypes.object.isRequired,
    showShareIcon: PropTypes.bool,
};

export default EventCardWithCTAs;
